import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="forgot-password-page">
      <div className="hidden lg:flex flex-col justify-between bg-navy grain p-14">
        <Logo light />
        <div>
          <h2 className="font-display text-4xl font-bold text-white leading-tight">Reset your access.</h2>
          <p className="mt-4 text-slate-400">We'll email you a secure link to set a new password.</p>
        </div>
        <p className="text-xs text-slate-500">An independent financial-market education & research platform.</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="mx-auto mb-5 h-14 w-14 rounded-full bg-gold/15 flex items-center justify-center">
                <MailCheck className="h-7 w-7 text-gold" />
              </div>
              <h1 className="font-display text-3xl font-bold text-navy">Check your email</h1>
              <p className="mt-3 text-sm text-slate-500">
                If an account exists for <span className="font-semibold text-navy">{email}</span>, we've sent a password
                reset link. The link expires in 60 minutes.
              </p>
              <p className="mt-6 text-sm text-slate-500">
                <Link to="/login" className="text-navy font-medium hover:text-gold" data-testid="go-login">Back to Login</Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-navy">Forgot password?</h1>
              <p className="mt-2 text-sm text-slate-500">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" placeholder="Email address" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60">
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <p className="mt-6 text-sm text-slate-500 text-center">
                Remembered it? <Link to="/login" className="text-navy font-medium hover:text-gold" data-testid="go-login">Login</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
